import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Download, AlertCircle, CheckCircle, FileText, X, Database, Trash2 } from 'lucide-react';
import { showNotification, showError } from '../../utils/notificationHandler';
import * as XLSX from 'xlsx';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';
import Card from '../shared/Card';

const ProductUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [productStats, setProductStats] = useState({ totalProducts: 0 });
  const [loadingStats, setLoadingStats] = useState(false);
  const [clearingProducts, setClearingProducts] = useState(false);

  // Fetch product statistics
  const fetchProductStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const response = await fetch('/api/products/stats');
      if (response.ok) {
        const data = await response.json();
        setProductStats(data);
      }
    } catch (error) {
      console.error('Error fetching product stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // Load stats on component mount
  React.useEffect(() => {
    fetchProductStats();
  }, [fetchProductStats]);

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'text/csv' || selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        setFile(selectedFile);
        setUploadResult(null);
      } else {
        showError(new Error('Invalid file type'), 'Please select a CSV or Excel file');
      }
    }
  };

  // Parse CSV/Excel file
  const parseFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsBinaryString(file);
    });
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      showError(new Error('No file selected'), 'Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      // Parse the file
      const rawData = await parseFile(file);
      
      // Filter out completely empty rows first
      const nonEmptyRows = rawData.filter((row, index) => {
        // Check if row has any meaningful data
        const hasData = Object.values(row).some(value => 
          value !== null && value !== undefined && String(value).trim() !== ''
        );
        return hasData;
      });

      console.log(`Found ${rawData.length} total rows, ${nonEmptyRows.length} non-empty rows`);

      if (nonEmptyRows.length === 0) {
        throw new Error('No data found in the file. Please check that your CSV/Excel file contains data.');
      }

      // Validate and transform data
      const products = [];
      const detailedErrors = [];

      nonEmptyRows.forEach((row, index) => {
        const actualRowNumber = rawData.indexOf(row) + 1; // Get original row number
        
        // Try different possible column names
        const partNumber = row['Part Number'] || row['PartNumber'] || row['part_number'] || row['PART_NUMBER'] || row['Part_Number'];
        const description = row['Description'] || row['DESCRIPTION'] || row['description'] || row['Part Description'] || row['PartDescription'];
        const designation = row['Designation'] || row['DESIGNATION'] || row['designation'] || row['Part Designation'] || row['PartDesignation'];
        const pictureFull = row['Picture Full'] || row['PictureFull'] || row['picture_full'] || row['PICTURE_FULL'] || row['Picture_Full'];
        const pictureMini = row['Picture Mini'] || row['PictureMini'] || row['picture_mini'] || row['PICTURE_MINI'] || row['Picture_Mini'];
        
        // Detailed validation with better error messages
        if (!partNumber || String(partNumber).trim() === '') {
          detailedErrors.push(`Row ${actualRowNumber}: Missing or empty 'Part Number'. Row data: ${JSON.stringify(row)}`);
          return;
        }

        if (!description || String(description).trim() === '') {
          detailedErrors.push(`Row ${actualRowNumber}: Missing or empty 'Description'. Row data: ${JSON.stringify(row)}`);
          return;
        }

        products.push({
          part_number: String(partNumber).trim(),
          description: String(description).trim(),
          designation: designation ? String(designation).trim() : null,
          picture_full: pictureFull ? String(pictureFull).trim() : null,
          picture_mini: pictureMini ? String(pictureMini).trim() : null
        });
      });

      if (detailedErrors.length > 0) {
        console.error('Validation errors found:', detailedErrors);
        throw new Error(`Found ${detailedErrors.length} validation errors:\n${detailedErrors.slice(0, 5).join('\n')}${detailedErrors.length > 5 ? `\n... and ${detailedErrors.length - 5} more errors` : ''}`);
      }

      if (products.length === 0) {
        throw new Error('No valid products found in the file');
      }

      // Upload to backend
      const response = await fetch('/api/products/bulk-upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ products }),
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      setUploadResult(result);

      if (result.success) {
        showNotification(`Successfully uploaded ${result.totalInserted} products!`, 'success');
        fetchProductStats(); // Refresh stats
      } else {
        showError(new Error('Upload partially failed'), `${result.totalInserted} products uploaded, ${result.totalErrors} errors`);
      }

    } catch (error) {
      console.error('Upload error:', error);
      showError(error, 'Failed to upload products');
      setUploadResult({
        success: false,
        error: error.message
      });
    } finally {
      setUploading(false);
    }
  };

  // Clear all products
  const handleClearProducts = async () => {
    if (!window.confirm('Are you sure you want to clear all products? This action cannot be undone.')) {
      return;
    }

    setClearingProducts(true);
    try {
      const response = await fetch('/api/products/clear', {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Clear failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        showNotification('All products cleared successfully', 'success');
        fetchProductStats(); // Refresh stats
        setUploadResult(null);
      } else {
        throw new Error(result.error || 'Failed to clear products');
      }

    } catch (error) {
      console.error('Clear error:', error);
      showError(error, 'Failed to clear products');
    } finally {
      setClearingProducts(false);
    }
  };

  // Download template
  const downloadTemplate = () => {
    const templateData = [
      { 
        'Part Number': 'P001', 
        'Description': 'Hydraulic Pump Assembly', 
        'Designation': 'HD-Series',
        'Picture Full': 'https://cdn.example.com/images/P001_full.jpg',
        'Picture Mini': 'https://cdn.example.com/images/P001_mini.jpg'
      },
      { 
        'Part Number': 'P002', 
        'Description': 'Pressure Relief Valve', 
        'Designation': 'PRV-Type',
        'Picture Full': 'https://cdn.example.com/images/P002_full.jpg',
        'Picture Mini': 'https://cdn.example.com/images/P002_mini.jpg'
      },
      { 
        'Part Number': 'P003', 
        'Description': 'Hydraulic Cylinder', 
        'Designation': '',
        'Picture Full': '',
        'Picture Mini': ''
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.writeFile(wb, "products_template.xlsx");
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <PageHeader
        title="Products"
        description="Upload a CSV or Excel file to add or update products in the catalogue, or clear the product database."
      />

      <Card>
        {/* Current Statistics */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Current Database</h3>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Database className="w-5 h-5 mr-2 text-gray-500" />
              <span className="text-gray-700">
                {loadingStats ? 'Loading...' : `${productStats.totalProducts.toLocaleString()} products in database`}
              </span>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={fetchProductStats}
              loading={loadingStats}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Upload Section */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-6 text-center">
          <div className="space-y-4">
            <div className="flex justify-center">
              <Upload className="w-16 h-16 text-gray-400" />
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Upload Product Database</h3>
              <p className="text-gray-600 mb-4">
                Upload a CSV or Excel file containing Part Number and Description columns
              </p>
              
              <input
                type="file"
                id="file-upload"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
              
              <div className="space-y-3">
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Choose File
                </label>
                
                {file && (
                  <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                    <FileText className="w-4 h-4" />
                    <span>{file.name}</span>
                    <button
                      onClick={() => setFile(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <Button
              size="lg"
              onClick={handleUpload}
              disabled={!file}
              loading={uploading}
              icon={<Upload className="w-5 h-5" />}
            >
              {uploading ? 'Uploading...' : 'Upload Products'}
            </Button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 justify-center">
          <Button
            variant="secondary"
            onClick={downloadTemplate}
            icon={<Download className="w-5 h-5" />}
          >
            Download Template
          </Button>

          <Button
            variant="danger"
            onClick={handleClearProducts}
            disabled={productStats.totalProducts === 0}
            loading={clearingProducts}
            icon={<Trash2 className="w-5 h-5" />}
          >
            Clear All Products
          </Button>
        </div>

        {/* Upload Results */}
        <AnimatePresence>
          {uploadResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mt-6"
            >
              <div className={`rounded-lg p-4 ${
                uploadResult.success 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start">
                  {uploadResult.success ? (
                    <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-red-600 mr-3 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h4 className={`font-semibold ${
                      uploadResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {uploadResult.success ? 'Upload Successful!' : 'Upload Failed'}
                    </h4>
                    
                    {uploadResult.success ? (
                      <div className="mt-2 text-sm text-green-700">
                        <p>Successfully processed {uploadResult.totalProcessed} products</p>
                        <p>Inserted/Updated: {uploadResult.totalInserted}</p>
                        {uploadResult.totalErrors > 0 && (
                          <p className="text-orange-600">Errors: {uploadResult.totalErrors}</p>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 text-sm text-red-700">
                        <p>{uploadResult.error}</p>
                      </div>
                    )}

                    {/* Show detailed errors if any */}
                    {uploadResult.validationErrors && uploadResult.validationErrors.length > 0 && (
                      <div className="mt-3">
                        <h5 className="font-medium text-red-800">Validation Errors:</h5>
                        <ul className="mt-1 text-sm text-red-700 list-disc list-inside max-h-32 overflow-y-auto">
                          {uploadResult.validationErrors.slice(0, 10).map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                          {uploadResult.validationErrors.length > 10 && (
                            <li>... and {uploadResult.validationErrors.length - 10} more errors</li>
                          )}
                        </ul>
                      </div>
                    )}

                    {uploadResult.batchErrors && uploadResult.batchErrors.length > 0 && (
                      <div className="mt-3">
                        <h5 className="font-medium text-red-800">Processing Errors:</h5>
                        <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                          {uploadResult.batchErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Instructions */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-gray-500" />
            Instructions
          </h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• File must be in CSV or Excel format (.csv, .xlsx, .xls)</li>
            <li>• Required columns: <code className="bg-gray-200 px-1 rounded">Part Number</code> and <code className="bg-gray-200 px-1 rounded">Description</code></li>
            <li>• Optional columns: <code className="bg-gray-200 px-1 rounded">Designation</code> (for additional part classification)</li>
            <li>• Optional columns: <code className="bg-gray-200 px-1 rounded">Picture Full</code> and <code className="bg-gray-200 px-1 rounded">Picture Mini</code> (for product images)</li>
            <li>• Picture Mini URLs will be displayed in autocomplete suggestions</li>
            <li>• Part numbers must be unique (duplicates will be updated)</li>
            <li>• Large files are processed in batches for better performance</li>
            <li>• Download the template file to see the correct format</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default ProductUpload;
